import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ImpactCategory = 'conferencia_taller' | 'proyecto' | 'caso_exito' | 'testimonio';

export interface ImpactRecord {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  category: ImpactCategory;
  image_url: string | null;
  video_url: string | null;
  location: string | null;
  company: string | null;
  date: string | null;
  featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function getYoutubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

export const IMPACT_CATEGORY_LABELS: Record<ImpactCategory, string> = {
  conferencia_taller: 'Conferencias y Talleres',
  proyecto: 'Proyectos',
  caso_exito: 'Casos de Éxito',
  testimonio: 'Testimonios',
};

export const IMPACT_CATEGORY_BADGE: Record<ImpactCategory, string> = {
  conferencia_taller: 'Conferencia/Taller',
  proyecto: 'Proyecto',
  caso_exito: 'Caso de Éxito',
  testimonio: 'Testimonio',
};

interface UseImpactRecordsOptions {
  category?: ImpactCategory;
  featuredOnly?: boolean;
  limit?: number;
}

export function useImpactRecords(options: UseImpactRecordsOptions = {}) {
  const { category, featuredOnly, limit } = options;
  return useQuery({
    queryKey: ['impact-records', { category, featuredOnly, limit }],
    queryFn: async () => {
      let q = supabase
        .from('impact_records' as any)
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (category) q = q.eq('category', category);
      if (featuredOnly) q = q.eq('featured', true);
      if (limit) q = q.limit(limit);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ImpactRecord[];
    },
  });
}

export function useManageImpactRecords() {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: async (payload: Partial<ImpactRecord>) => {
      const { data, error } = await (supabase as any)
        .from('impact_records')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as ImpactRecord;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['impact-records'] });
      toast.success('Registro de impacto creado');
    },
    onError: (e: any) => toast.error(e.message ?? 'Error al crear registro'),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...payload }: Partial<ImpactRecord> & { id: string }) => {
      const { data, error } = await (supabase as any)
        .from('impact_records')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as ImpactRecord;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['impact-records'] });
      toast.success('Registro actualizado');
    },
    onError: (e: any) => toast.error(e.message ?? 'Error al actualizar'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('impact_records').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['impact-records'] });
      toast.success('Registro eliminado');
    },
    onError: (e: any) => toast.error(e.message ?? 'Error al eliminar'),
  });

  const uploadImage = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop();
    const path = `impact/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('content').upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from('content').getPublicUrl(path);
    return data.publicUrl;
  };

  return { create, update, remove, uploadImage };
}
